import * as React from 'react';
import {BaseWidgetProps} from '../widget-container';
import {KbHero} from './kb-hero/kb-hero.comp';
import {NavigationState} from '../../common/navigator';
import {getIdealTextColor} from '@wix/answers-lib';
import {EventType} from '../../common/logger/event-type';
import {Article} from '@wix/answers-api';
import {classNames} from '../../common/utils';
import { WidgetMessage, WidgetMessageType } from '../../common/message-types';
import { sanitizeExceptWidgetSdk } from '@wix/answers-widget-viewer';

export type KBContainerProps = {
	baseWidgetProps: BaseWidgetProps,
	searchTerm: string,
	article: Article | null;
};

export type KBContainerState = {
	brandColor: string;
	isCovered: boolean;
	isSearchFocused: boolean;
	moduleSettings: any;
	customHeader?: string;
};

export class KBContainer extends React.Component<KBContainerProps, KBContainerState> {
	innerContent: Element | null;

	state: KBContainerState = {
		brandColor: '',
		isCovered: false,
		isSearchFocused: false,
		moduleSettings: null,
		customHeader: this.props.baseWidgetProps.messageHistory.getCustomHeader()
			? sanitizeExceptWidgetSdk(this.props.baseWidgetProps.messageHistory.getCustomHeader() || '')
			: ''
	};

	componentDidMount () {
		if (window) {
			window.addEventListener('message', this.handleSettingsPostMessage);
		}
	}

	componentWillUnmount () {
		if (window) {
			window.removeEventListener('message', this.handleSettingsPostMessage);
		}
	}

	componentWillMount () {
		const {widgetConfig} = this.props.baseWidgetProps;

		this.setState({ brandColor: widgetConfig.appearance.brandColor });
		this.setState({ moduleSettings: widgetConfig.kbModuleSettings });
	}

	componentDidUpdate () {
		if (this.innerContent && !this.props.baseWidgetProps.isPreview) {
			this.innerContent.scrollTop = 0;
		}
	}

	handleSettingsPostMessage = (event: MessageEvent) => {
		const newSettings: any = event.data.previewNewSettings;
		if (newSettings) {
			const brandColor = newSettings.color || this.state.brandColor;
			const moduleSettings = newSettings.kbModule || this.state.moduleSettings;
			this.setState({ brandColor, moduleSettings });
		}
		const widgetMessage: WidgetMessage = event.data.__ansWidget;
		if (widgetMessage) {
			switch (widgetMessage.type) {
				case WidgetMessageType.SET_CUSTOM_KB_HEADER:
					const cleanHtml = sanitizeExceptWidgetSdk(widgetMessage.data.headerHtml);
					this.setState({customHeader: cleanHtml});
					break;
				default:
					break;
			}
		}
	}

	onSearch = (searchTerm: string, prevSearchTerm: string) => {
		if (searchTerm === '') {
			this.props.baseWidgetProps.logger.logEvent(EventType.SEARCH_IGNORED, {text: prevSearchTerm});
			this.props.baseWidgetProps.widgetNavigator.goBackBeforeSearch();
		} else {
			this.props.baseWidgetProps.widgetNavigator.goToState(NavigationState.search, searchTerm);
		}
	}

	onSearchFocusChanged = (isSearchFocused: boolean) => {
		this.setState({isSearchFocused});
	}

	onWheelScroll = (e: any) => {
		if (this.props.baseWidgetProps.widgetNavigator.getQueryParam('allow_outer_scroll') !== 'true') {
			this.blockOuterScroll(e);
		}
	}

	blockOuterScroll = (e: any) => {
		const contentNode = e.currentTarget;
		const totalHeight = contentNode.scrollHeight;
		const maxScroll = totalHeight - contentNode.offsetHeight;
		const delta = e.deltaY % 3 ? (e.deltaY) : (e.deltaY * 10);
		if (contentNode.scrollTop + delta <= 0) {
			contentNode.scrollTop = 0;
			e.preventDefault();
		} else if (contentNode.scrollTop + delta >= maxScroll) {
			contentNode.scrollTop = maxScroll;
			e.preventDefault();
		}
		e.stopPropagation();
	}

	setRef = (ref: HTMLDivElement) => {
		if (ref) {
			this.innerContent = ref;
			ref.addEventListener('wheel', this.onWheelScroll, {passive: false});
		} else if (this.innerContent) {
			this.innerContent.removeEventListener('wheel', this.onWheelScroll);
		}
	}

	render () {
		const props = this.props;
		const baseWidgetProps = props.baseWidgetProps;
		const translate = baseWidgetProps.translate;
		const heroStyle = {
			backgroundColor: this.state.brandColor,
			color: getIdealTextColor(this.state.brandColor)
		};
		const moduleSettings = this.state.moduleSettings || baseWidgetProps.widgetConfig.kbModuleSettings;

		const header = this.state.customHeader ? (
			<header className='custom-kb-header' dangerouslySetInnerHTML={{__html: this.state.customHeader}}/>
		) : (
			<header className='hero-container' style={heroStyle}>
				<KbHero
					searchTerm={props.searchTerm}
					moduleSettings={moduleSettings}
					onSearchChange={this.onSearch}
					baseWidgetProps={baseWidgetProps}
					onSearchFocusChanged={this.onSearchFocusChanged}
				/>
			</header>
		);

		const classnames = classNames(
			`widget-module kb-container`,
			{
				blurred: this.state.isCovered,
				'kb-container-with-back-button': baseWidgetProps.widgetNavigator.isKbModuleHasBack(),
				'kb-home-page': baseWidgetProps.widgetNavigator.isKbHomepage()
			}
		);

		return (
			<div
				className={classnames}
				data-item-id={this.props.article ? this.props.article.id : ''}
			>
				{header}
				<div
					className={`inner-content${this.state.isSearchFocused && !props.searchTerm ? ' focused' : ''}`}
					ref={this.setRef}
				>
					{props.children}
				</div>
				<footer className='footer-container'>
					<a href='http://wixanswers.com' target='_blank'>
						{translate('widget-ng.footer.powerd-by')}<span>Wix Answers</span>
					</a>
				</footer>
			</div>
		);
	}
}
